<template>
  <div
    :id="id"
    class="tinymce"
    :class="{ 'text-danger': validationError }"
  >
    <slot />

    <template v-if="label">
      <h2 v-if="isDocumentContext">
        {{ label }}
      </h2>

      <label v-else>
        {{ label }}

        <span
          v-if="required"
          class="text-danger"
        >
          *
        </span>
      </label>
    </template>

    <Editor
      :model-value="model"
      class="editor-root"
      :init="editorConfig"
      @update:model-value="handleContentUpdate"
    />

    <small class="pl-1 flex justify-between items-center">
      <span v-if="hint">{{ hint }}</span>
      <span
        v-if="wordLimit && totalWordCount > (wordLimit - (isDocumentContext ? 500 : 50))"
        class="word-limit text-danger"
      >
        <template v-if="isDocumentContext">
          Maximum word count: {{ totalWordCount }}/{{ wordLimit }} words
        </template>

        <template v-else>
          {{ currentWordCount }} / {{ wordLimit }} words
        </template>
      </span>
    </small>

    <span
      v-if="validationError"
      class="text-danger text-xs"
    >
      {{ validationError }}
    </span>
  </div>
</template>

<script setup>
import { ref, computed, inject } from 'vue';
import axios from 'axios';

import 'tinymce';
import 'tinymce/icons/default/icons.min.js';
import 'tinymce/themes/silver/theme.min.js';
import 'tinymce/models/dom/model.min.js';
import 'tinymce/skins/ui/oxide/skin.js';
import 'tinymce/icons/default';
import 'tinymce/models/dom/model';
import 'tinymce/plugins/link';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/image';
import 'tinymce/plugins/code';
import 'tinymce/themes/silver';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/table';
import 'tinymce/skins/ui/oxide/content.js';
import 'tinymce/skins/content/default/content.js';

import Editor from '@tinymce/tinymce-vue';
import { useDomPurify, useToasts } from '@/composables';
import { usePlanStore } from '@/store';

const isDocumentContext = inject('isDocumentContext', false);
const model = defineModel();
const planStore = usePlanStore();
const domPurify = useDomPurify();
const toasts = useToasts();

const emit = defineEmits(['update:wordCount', 'update:captions']);

const props = defineProps({
  id: { type: String, required: true },
  label: { type: String, default: '' },
  hint: { type: String, default: '' },
  inline: { type: Boolean, default: undefined },
  validationError: { type: String, default: null },
  placeholder: { type: String, default: null },
  height: { type: Number, default: 350 },
  wordLimit: { type: Number, default: null },
  totalWordCount: { type: Number, default: 0 },
  allowImageUpload: { type: Boolean, default: true },
  required: Boolean,
});

const isReady = ref(false);
const currentWordCount = ref(0);

const plugins = ['link', 'autolink', 'lists', 'wordcount', 'table'];
let toolbar = 'blocks bold italic bullist numlist link table code';

if (props.allowImageUpload) {
  plugins.push('image');
  toolbar += ' image';
}

const captionPlaceholderText = 'Insert figure name here';
const captionPlaceholderClass = 'placeholder-caption';

const css = `
  @font-face { font-family: 'Inter'; font-weight: 300; src: url('/fonts/inter/inter-v13-latin-300.woff2') format('woff2'); }
  @font-face { font-family: 'Inter'; font-weight: 400; src: url('/fonts/inter/inter-v13-latin-regular.woff2') format('woff2'); }
  @font-face { font-family: 'Inter'; font-weight: 500; src: url('/fonts/inter/inter-v13-latin-500.woff2') format('woff2'); }
  @font-face { font-family: 'Inter'; font-weight: 600; src: url('/fonts/inter/inter-v13-latin-600.woff2') format('woff2'); }
  @font-face { font-family: 'Inter'; font-weight: 700; src: url('/fonts/inter/inter-v13-latin-700.woff2') format('woff2'); }

  body { font-family: 'Inter', sans-serif; font-size: 14px; padding: 12px; line-height: 1.8; }
  .mce-content-body[data-mce-placeholder]::before { content: attr(data-mce-placeholder); position: absolute; cursor: text; color: #9ca3af; }

  h1 { font-weight: 600 !important; color: black !important; font-size: 19px !important; margin-top: 0 !important; margin-bottom: 1mm !important; }
  h2, h3 { font-weight: 600 !important; color: black !important; font-size: 15px !important; margin: 0 !important; }
  p { margin: 0; }
  .mce-content-body p, .mce-content-body li { text-align: justify; }

  table { border-collapse: collapse; width: 100%; }
  table td, table th { border: 1px solid #ccc; padding: 0.4rem; min-width: 1em; vertical-align: top; }

  figure { display: block; width: 100%; margin-bottom: 1em; }
  figure img { display: block; width: 100%; max-width: 100%; height: auto; }
  figure.figure-half-left { width: 50%; float: left; margin-right: 1.5em; }
  figure.figure-half-right { width: 50%; float: right; margin-left: 1.5em; }
  figure figcaption { display: block; box-sizing: border-box; width: 100%; border: 1px solid #C0C0C0; border-radius: 2px; margin-top: 0.75rem; padding: 7px 8px; min-height: 36px; line-height: 20px; outline: none; cursor: text; text-align: left; color: #000; }

  /* Explicitly prevent bold placeholder text */
  figure figcaption.placeholder-caption {
    color: #9F9F9F;
    font-weight: 400 !important;
    font-style: normal !important;
  }

  figure figcaption > p, figure figcaption > div { margin: 0; padding: 0; display: block; }
  [data-mce-caret] { display: none !important; }
`;

const isInline = computed(() => props.inline !== undefined ? props.inline : isDocumentContext);
const placeholderText = computed(() => props.placeholder ?? (isDocumentContext ? 'Start typing your message here' : ''));

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

const handleImageUpload = async (blobInfo, progress) => {
  if (blobInfo.blob().size > MAX_IMAGE_SIZE) {
    throw new Error('Image is too large. Please upload a smaller image (max 10MB).');
  }

  const formData = new FormData();
  formData.append('image', blobInfo.blob());

  try {
    const response = await axios({
      onUploadProgress: event => progress((event.loaded / event.total) * 100),
      data: formData,
      url: '/images',
      method: 'POST',
    });

    return response.data.data.url;
  } catch (error) {
    const status = error.response?.status;
    if (status === 422 || status === 413) throw new Error('Image is too large. Please upload a smaller image (max 10MB).');
    if (status === 415) throw new Error('Unsupported image format. Please upload a JPEG, PNG, or WebP.');
    throw new Error('Image upload failed. Please try again.');
  }
};

const handleContentUpdate = (newValue) => {
  if (isReady.value) {
    model.value = newValue;
  }
};

const editorConfig = computed(() => ({
  height: props.height,
  menubar: false,
  inline: isInline.value,
  contextmenu: false,
  branding: false,
  elementpath: false,
  theme: 'silver',
  toolbar_mode: 'sliding',
  fixed_toolbar_container: '#tinymce-toolbar',
  placeholder: placeholderText.value,

  apiKey: false,
  license_key: 'gpl',
  plugins: plugins,
  toolbar: toolbar,

  convert_urls: false,
  relative_urls: false,
  remove_script_host: false,
  block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3;',
  content_style: css,

  // Superscripts the word just typed when it is an area or volume unit (km2, 1.5m3) or an ordinal (12th).
  // The zero-width space after the sup gives the caret somewhere outside it, so backspacing over the
  // following space doesn't leave the user typing in superscript.
  text_patterns_lookup: ({ text }) => {
    const unit = text.match(/(?:^|[\s\d.,])(k?m)([23])$/);
    const ordinal = text.match(/(?:^|\s)([\d,]*\d)(st|nd|rd|th)$/);
    const [, base, superscript] = unit ?? ordinal ?? [];

    if (!base) return [];

    return [{ start: base + superscript, replacement: `${base}<sup>${superscript}</sup>&#8203;` }];
  },

  fill_empty_blocks: false,
  verify_html: false,
  entity_encoding: 'raw',
  // [class] must be listed here so TinyMCE preserves the placeholder-caption class on serialisation.
  extended_valid_elements: 'strong/b,em/i,u,span[style],figure[class],figcaption[class]',

  /* Explicitly forbid figure/caption inside formatting tags */
  valid_children: '-strong[figure|figcaption],-b[figure|figcaption],-em[figure|figcaption],-i[figure|figcaption],-span[figure|figcaption]',

  file_picker_types: 'image',
  image_description: false,
  image_dimensions: false,
  image_caption: false,
  paste_data_images: props.allowImageUpload,
  images_upload_handler: handleImageUpload,

  smart_paste: true,
  paste_merge_formats: false,
  paste_webkit_styles: 'all',

  paste_preprocess: function(editor, args) {
    if (props.wordLimit) {
      const pastedWordCount = args.content.replace(/<[^>]*>/g, ' ').split(/\s+/).filter(Boolean).length;
      if (props.totalWordCount + pastedWordCount > props.wordLimit) {
        args.content = '';
        toasts.error('Word limit reached.');
        return;
      }
    }

    if (!props.allowImageUpload) {
      args.content = args.content.replace(/<img[^>]*>/gi, '');
    }

    // Include aria-level checks for Google Docs compatibility
    const isWordPaste = /Mso|BCX|SCXW|OutlineElement/i.test(args.content) || args.content.includes('data-aria-level') || args.content.includes('aria-level');
    editor._isWordPaste = isWordPaste;
    editor.isGoogleDocsPaste = args.content.includes('docs-internal-guid');

    if (isWordPaste) {
      const pasteContainer = document.createElement('div');
      pasteContainer.innerHTML = args.content;

      // Word Online wraps each <ul> in its own <div>, preventing the adjacent
      // merge below from seeing them as siblings. Unwrap any div that contains
      // only list elements so the merge can reach across them.
      Array.from(pasteContainer.querySelectorAll('div')).reverse().forEach(div => {
        if (!div.parentNode || div.closest('table')) return;
        const children = Array.from(div.children);
        if (children.length > 0 && children.every(c => c.tagName === 'UL' || c.tagName === 'OL')) {
          while (div.firstChild) div.parentNode.insertBefore(div.firstChild, div);
          div.remove();
        }
      });

      const lists = Array.from(pasteContainer.querySelectorAll('ul, ol'));
      lists.forEach(list => {
        if (!list.parentNode) return;
        let next = list.nextSibling;
        while (next) {
          if (next.nodeType === 3 && next.textContent.trim() === '') {
            next = next.nextSibling;
            continue;
          }
          if (next.nodeType === 1 && next.tagName === 'P' && next.innerHTML.trim() === '') {
            next = next.nextSibling;
            continue;
          }
          if (next.nodeType === 1 && next.tagName === list.tagName) {
            while (next.firstChild) list.appendChild(next.firstChild);
            const toRemove = next;
            next = next.nextSibling;
            toRemove.remove();
          } else {
            break;
          }
        }
      });

      pasteContainer.querySelectorAll('ul, ol').forEach(list => {
        const items = Array.from(list.querySelectorAll('li'));
        if (items.length === 0) return;

        const newRoot = document.createElement(list.tagName);
        const stack = [{ list: newRoot, depth: 1 }];

        items.forEach(li => {
          // Fallback checks for regular aria-level
          let depth = parseInt(li.getAttribute('aria-level') || li.getAttribute('data-aria-level'), 10) || 1;
          const cleanLi = document.createElement('li');
          cleanLi.innerHTML = li.innerHTML;
          cleanLi.querySelectorAll('ul, ol').forEach(n => n.remove());

          if (depth > stack[stack.length - 1].depth) {
            while (depth > stack[stack.length - 1].depth) {
              const lastLi = stack[stack.length - 1].list.lastElementChild;
              if (!lastLi) break;
              const subList = document.createElement(list.tagName);
              lastLi.appendChild(subList);
              stack.push({ list: subList, depth: stack[stack.length - 1].depth + 1 });
            }
          } else if (depth < stack[stack.length - 1].depth) {
            while (stack.length > 1 && depth < stack[stack.length - 1].depth) {
              stack.pop();
            }
          }
          stack[stack.length - 1].list.appendChild(cleanLi);
        });
        list.parentNode.replaceChild(newRoot, list);
      });
      args.content = pasteContainer.innerHTML;
    }

    const hasTags = /<[a-z][\s\S]*>/i.test(args.content);
    if (!hasTags) {
      if (args.content.includes('\n\n')) {
        const lines = args.content.split(/\n\n+/);
        args.content = lines
          .map(line => `<p>${line.replace(/\n/g, '<br>')}</p>`)
          .join('<p><br></p>');
      } else {
        args.content = args.content.replace(/\n/g, '<br>');
      }
    }
  },

  paste_postprocess: function(editor, args) {
    try {
      const isGoogleDocs = editor.isGoogleDocsPaste;

      if (isGoogleDocs) {
        Array.from(args.node.querySelectorAll('b, strong, span')).forEach(el => {
          if (/font-weight:\s*(normal|400)/i.test(el.getAttribute('style') || '')) {
            el.outerHTML = el.innerHTML;
          }
        });
      }

      args.node.querySelectorAll('a').forEach(a => {
        const text = (a.textContent || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
        if (/^edit$/i.test(text)) {
          let blockParent = a.closest('p, div, h1, h2, h3, h4, h5, h6');
          if (blockParent) blockParent.setAttribute('data-wiki-heading', 'true');
          let directParent = a.parentNode;
          a.remove();
          if (directParent) directParent.innerHTML = directParent.innerHTML.replace(/\[\s*[\u200B-\u200D\uFEFF]*\s*\]/g, '');
        }
      });

      args.node.querySelectorAll('a').forEach(link => {
        if (!link.parentNode) return;
        if (link.querySelector('img, p, div, h1, h2, h3, h4, h5, h6, table, ul, ol')) {
          link.outerHTML = link.innerHTML;
        }
      });

      args.node.querySelectorAll('figcaption a').forEach(link => {
        if (!link.parentNode) return;
        link.outerHTML = link.innerHTML;
      });

      args.node.querySelectorAll('[style*="pre-wrap"]').forEach(el => {
        if (!el.parentNode) return;
        const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null, false);
        const textNodes = [];
        let n; while (n = walker.nextNode()) textNodes.push(n);

        textNodes.forEach(node => {
          if (node.nodeValue.includes('\n')) {
            const frag = document.createDocumentFragment();
            const parts = node.nodeValue.split('\n');
            parts.forEach((part, i) => {
              frag.appendChild(document.createTextNode(part));
              if (i < parts.length - 1) frag.appendChild(document.createElement('br'));
            });
            if (node.parentNode) node.parentNode.replaceChild(frag, node);
          }
        });
      });

      args.node.querySelectorAll('p, div, h1, h2, h3, h4, h5, h6').forEach(el => {
        if (!el.parentNode || el.querySelector('p, div, table, ul, ol')) return;
        const rawText = (el.textContent || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
        if (rawText.length === 0) return;

        let currentTag = el.tagName.toLowerCase();
        let targetTag = currentTag;
        const className = (el.className || '').toLowerCase().replace(/[^a-z0-9]/g, '');

        if (el.getAttribute('data-wiki-heading') === 'true' || el.querySelector('.mw-headline')) {
          if (currentTag === 'h3') targetTag = 'h2';
          else if (currentTag === 'h4') targetTag = 'h3';
          else targetTag = 'h1';
        }
        else if (className.includes('heading1') || className === 'h1' || (className.includes('title') && !className.includes('subtitle'))) targetTag = 'h1';
        else if (className.includes('heading2') || className === 'h2' || className.includes('subtitle')) targetTag = 'h2';
        else if (className.includes('heading3') || className === 'h3') targetTag = 'h3';

        if (targetTag && currentTag !== targetTag) {
          const newTag = document.createElement(targetTag);
          newTag.innerHTML = el.innerHTML;
          if (el.getAttribute('style')) newTag.setAttribute('style', el.getAttribute('style'));
          el.parentNode.replaceChild(newTag, el);
        }
      });

      args.node.querySelectorAll('li > p').forEach(p => {
        if (!p.parentNode) return;
        p.outerHTML = p.innerHTML || '<br>';
      });

      args.node.querySelectorAll('table').forEach(table => {
        if (!table.parentNode) return;
        const rows = table.querySelectorAll('tr');
        let maxCols = 0;
        rows.forEach(r => { if (r.children.length > maxCols) maxCols = r.children.length; });

        if (rows.length === 1 && maxCols === 1) {
          const cell = table.querySelector('td, th');
          if (cell) {
            const frag = document.createDocumentFragment();
            if (cell.querySelector('p, h1, h2, h3, h4, div')) {
              while (cell.firstChild) frag.appendChild(cell.firstChild);
            } else {
              const parts = cell.innerHTML.split(/<br\s*\/?>/i);
              parts.forEach(part => {
                const cleanPart = part.trim();
                if (cleanPart) {
                  const p = document.createElement('p');
                  p.innerHTML = cleanPart;
                  frag.appendChild(p);
                }
              });
            }
            table.parentNode.replaceChild(frag, table);
          }
        }
      });

      Array.from(args.node.querySelectorAll('div')).reverse().forEach(div => {
        if (!div.parentNode || div.closest('table')) return;
        if (div.querySelector('p, h1, h2, h3, h4, h5, h6, ul, ol, table, div')) {
          div.outerHTML = div.innerHTML;
        } else {
          const p = document.createElement('p');
          p.innerHTML = div.innerHTML;
          if (div.getAttribute('style')) p.setAttribute('style', div.getAttribute('style'));
          div.parentNode.replaceChild(p, div);
        }
      });

      // Website paste often dumps a whole block into one <p> with <br> line breaks. Under justified
      // text every line followed by a <br> gets stretched (only a block's true last line stays
      // ragged), so those lines show ugly gaps. Split such paragraphs at their <br>s into separate
      // paragraphs — the spacer pass below then treats them like any normal multi-paragraph paste.
      Array.from(args.node.querySelectorAll('p')).forEach(paragraph => {
        if (!paragraph.parentNode || paragraph.closest('table') || !paragraph.querySelector('br')) return;

        const lines = [[]];

        Array.from(paragraph.childNodes).forEach(node => {
          if (node.nodeName === 'BR') {
            lines.push([]);
          } else {
            lines[lines.length - 1].push(node);
          }
        });

        const fragment = document.createDocumentFragment();

        lines.forEach(line => {
          // Skip empty runs so leading/trailing and consecutive <br><br> collapse to a single break.
          const hasContent = line.some(node => (node.textContent || '').trim() !== '' || node.nodeName === 'IMG');
          if (!hasContent) return;

          const newParagraph = document.createElement('p');
          line.forEach(node => newParagraph.appendChild(node));
          fragment.appendChild(newParagraph);
        });

        if (fragment.childNodes.length) paragraph.parentNode.replaceChild(fragment, paragraph);
      });

      args.node.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach(heading => {
        if (!heading.parentNode) return;
        const prev = heading.previousElementSibling;
        if (prev && prev.innerHTML !== '<br>') {
          const prevText = (prev.textContent || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
          const prevHasMedia = prev.querySelector('img, table, figure, iframe');
          if (prevText !== '' || prevHasMedia) {
            const spacer = document.createElement('p');
            spacer.innerHTML = '<br>';
            heading.parentNode.insertBefore(spacer, heading);
          }
        }
        const next = heading.nextElementSibling;
        if (next && next.innerHTML !== '<br>') {
          const nextText = (next.textContent || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
          const nextHasMedia = next.querySelector('img, table, figure, iframe');
          if (nextText !== '' || nextHasMedia) {
            const spacer = document.createElement('p');
            spacer.innerHTML = '<br>';
            heading.parentNode.insertBefore(spacer, heading.nextSibling);
          }
        }
      });

      const children = Array.from(args.node.children);
      for (let i = 0; i < children.length - 1; i++) {
        const current = children[i];
        const next = children[i + 1];
        if (current.tagName === 'P' && next.tagName === 'P') {
          const currentText = (current.textContent || '').trim();
          const nextText = (next.textContent || '').trim();
          const currentHasMedia = current.querySelector('img, table, figure, iframe');
          const nextHasMedia = next.querySelector('img, table, figure, iframe');
          if ((currentText !== '' || currentHasMedia) && (nextText !== '' || nextHasMedia)) {
            const spacer = document.createElement('p');
            spacer.innerHTML = '<br>';
            args.node.insertBefore(spacer, next);
          }
        }
      }

      Array.from(args.node.querySelectorAll('span, b, i, u, strong, em')).reverse().forEach(el => {
        if (!el.parentNode) return;
        const styleStr = (el.getAttribute('style') || '').toLowerCase();
        const tag = el.tagName;

        const isBold = /font-weight:\s*(700|bold|bolder|[7-9]00)/.test(styleStr) ||
            ((tag === 'B' || tag === 'STRONG') && !/font-weight:\s*(normal|400)/.test(styleStr));
        const isItalic = /font-style:\s*italic/.test(styleStr) ||
            ((tag === 'I' || tag === 'EM') && !/font-style:\s*normal/.test(styleStr));
        const isUnderline = /text-decoration:\s*underline/.test(styleStr) ||
            ((tag === 'U') && !/text-decoration:\s*none/.test(styleStr));

        let newHtml = el.innerHTML;

        const hasBlockChildren = el.querySelector('p, div, h1, h2, h3, h4, h5, h6, ul, ol, li, table, figure, figcaption, img');

        if (!hasBlockChildren) {
          const trimmed = newHtml.trim();
          if (isUnderline && !(trimmed.startsWith('<u>') && trimmed.endsWith('</u>'))) newHtml = `<u>${newHtml}</u>`;
          if (isItalic && !(trimmed.startsWith('<em>') && trimmed.endsWith('</em>'))) newHtml = `<em>${newHtml}</em>`;
          if (isBold && !(trimmed.startsWith('<strong>') && trimmed.endsWith('</strong>'))) newHtml = `<strong>${newHtml}</strong>`;
          el.outerHTML = newHtml;
        } else {
          // Explicitly unwrap if it contains blocks like figure
          el.outerHTML = newHtml;
        }
      });

      args.node.querySelectorAll('p, h1, h2, h3').forEach(el => {
        const text = (el.textContent || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
        const hasMedia = el.querySelector('img, table, figure, iframe, br');
        if (text === '' && !hasMedia) {
          el.innerHTML = '<br>';
        } else if (text !== '') {
          el.innerHTML = el.innerHTML.replace(/&nbsp;|\u00A0/g, ' ');
          if (el.tagName !== 'P') {
            el.innerHTML = el.innerHTML.replace(/[\u200B-\u200D\uFEFF]/g, '');
          }
        }
      });

      args.node.querySelectorAll('sup, sub, small, a').forEach(el => {
        if (!el.parentNode) return;
        if (el.closest('h1, h2, h3, h4, h5, h6')) return;
        el.insertAdjacentHTML('afterend', '&#8203;');
      });

      const nodeChildren = Array.from(args.node.children);
      for (let i = nodeChildren.length - 1; i >= 0; i--) {
        const child = nodeChildren[i];
        if (child.tagName === 'P' && (child.innerHTML === '<br>' || child.innerHTML === '')) {
          child.remove();
        } else {
          break;
        }
      }

      const BULLET_RE = editor._isWordPaste
        ? /^[·•◦▪‣o\-\*]\s+/
        : /^[·•◦▪‣]\s*/;
      let bNode = args.node.firstChild;
      while (bNode) {
        const isBulletP = bNode.nodeName === 'P' && BULLET_RE.test((bNode.textContent || '').trimStart());
        if (!isBulletP) { bNode = bNode.nextSibling; continue; }
        const ul = document.createElement('ul');
        args.node.insertBefore(ul, bNode);
        let cur = bNode;
        while (cur) {
          const isEmpty = cur.nodeName === 'P' && (cur.innerHTML === '<br>' || cur.innerHTML === '');
          const isBullet = cur.nodeName === 'P' && BULLET_RE.test((cur.textContent || '').trimStart());
          if (isBullet) {
            const li = document.createElement('li');
            li.innerHTML = cur.innerHTML;
            const firstText = document.createTreeWalker(li, NodeFilter.SHOW_TEXT).nextNode();
            if (firstText) firstText.textContent = firstText.textContent.replace(BULLET_RE, '');
            ul.appendChild(li);
            const nextCur = cur.nextSibling;
            cur.remove();
            cur = nextCur;
          } else if (isEmpty) {
            const nextCur = cur.nextSibling;
            cur.remove();
            cur = nextCur;
          } else {
            break;
          }
        }
        bNode = cur;
      }
    } catch (error) {
      console.error('Paste Cleanup Failed:', error);
    } finally {
      args.node.querySelectorAll('*').forEach(el => {
        el.removeAttribute('style');
        el.removeAttribute('class');
        el.removeAttribute('dir');
        el.removeAttribute('data-mce-style');
        el.removeAttribute('data-wiki-heading');
        el.removeAttribute('data-aria-level');
        el.removeAttribute('aria-level'); // Clean up standard aria-level
        el.removeAttribute('data-aria-posinset');
        el.removeAttribute('data-listid');
        el.removeAttribute('data-font');
        el.removeAttribute('data-leveltext');
      });
      args.node.innerHTML = domPurify.sanitize(args.node.innerHTML);
    }
  },
  setup: (editor) => {
    editor.ui.registry.addIcon('figure-full', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><rect x="2" y="5" width="20" height="12" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><line x1="2" y1="21" x2="22" y2="21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>');
    editor.ui.registry.addIcon('figure-half-left', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><rect x="2" y="4" width="9" height="13" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><line x1="13" y1="7" x2="22" y2="7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="13" y1="11" x2="22" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="13" y1="15" x2="19" y2="15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="2" y1="21" x2="22" y2="21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>');
    editor.ui.registry.addIcon('figure-half-right', '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24"><rect x="13" y="4" width="9" height="13" rx="1.5" fill="none" stroke="currentColor" stroke-width="1.5"/><line x1="2" y1="7" x2="11" y2="7" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="2" y1="11" x2="11" y2="11" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="2" y1="15" x2="8" y2="15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><line x1="2" y1="21" x2="22" y2="21" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>');

    const getFigure = () => {
      const node = editor.selection.getNode();
      return node.nodeName === 'FIGURE' ? node : editor.dom.getParent(node, 'figure');
    };

    const applyFigureSize = (sizeClass) => {
      const figure = getFigure();

      if (!figure) return;

      editor.undoManager.transact(() => {
        editor.dom.removeClass(figure, 'figure-half-left');
        editor.dom.removeClass(figure, 'figure-half-right');
        if (sizeClass) editor.dom.addClass(figure, sizeClass);
      });

      editor.fire('input');
    };

    editor.ui.registry.addToggleButton('figureFull', {
      icon: 'figure-full',
      tooltip: 'Full width',
      onAction: () => applyFigureSize(null),
      onSetup: (api) => {
        const update = () => {
          const figure = getFigure();
          api.setActive(!!figure && !figure.classList.contains('figure-half-left') && !figure.classList.contains('figure-half-right'));
        };
        editor.on('NodeChange', update);
        return () => editor.off('NodeChange', update);
      },
    });

    editor.ui.registry.addToggleButton('figureHalfLeft', {
      icon: 'figure-half-left',
      tooltip: 'Half width - text wraps right',
      onAction: () => applyFigureSize('figure-half-left'),
      onSetup: (api) => {
        const update = () => {
          const figure = getFigure();
          api.setActive(!!figure && figure.classList.contains('figure-half-left'));
        };
        editor.on('NodeChange', update);
        return () => editor.off('NodeChange', update);
      },
    });

    editor.ui.registry.addToggleButton('figureHalfRight', {
      icon: 'figure-half-right',
      tooltip: 'Half width - text wraps left',
      onAction: () => applyFigureSize('figure-half-right'),
      onSetup: (api) => {
        const update = () => {
          const figure = getFigure();
          api.setActive(!!figure && figure.classList.contains('figure-half-right'));
        };
        editor.on('NodeChange', update);
        return () => editor.off('NodeChange', update);
      },
    });

    editor.ui.registry.addContextToolbar('figureSizing', {
      predicate: (node) => node.nodeName === 'FIGURE' || !!editor.dom.getParent(node, 'figure'),
      items: 'figureFull figureHalfLeft figureHalfRight',
      scope: 'node',
      position: 'selection',
    });

    editor.on('keyup', (e) => {
      if (e.key === 'Backspace' || e.key === 'Delete') {
        const node = editor.selection.getNode();
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const inView = rect.bottom > 0 && rect.top < window.innerHeight;
        if (!inView) node.scrollIntoView({ block: 'center' });
      }
    });

    // Allow cursor to exit A tags natively
    editor.on('ExecCommand', (e) => {
      if (e.command === 'mceInsertLink') {
        const node = editor.selection.getNode();
        if (node && node.nodeName === 'A') {
          node.insertAdjacentHTML('afterend', '&#8203;');
          const nextNode = node.nextSibling;
          if (nextNode) {
            editor.selection.setCursorLocation(nextNode, 1);
          }
        }
      }
    });

    const cleanupEmptyFigures = () => {
      const figures = editor.dom.select('figure');
      figures.forEach(figure => {
        const img = editor.dom.select('img', figure);
        if (img.length === 0) editor.dom.remove(figure);
      });
    };

    // TinyMCE's image-plugin and inline-boundary logic can duplicate an entire figure on click — dedupe by src.
    const cleanupDuplicateFigures = () => {
      const seenSrcs = new Set();
      editor.dom.select('figure').forEach(figure => {
        const img = figure.querySelector('img');
        const src = img?.getAttribute('src');
        if (!src) return;
        if (seenSrcs.has(src)) {
          editor.dom.remove(figure);
        } else {
          seenSrcs.add(src);
        }
      });
    };

    const autoCaptionImages = () => {
      let changed = false;
      const figuredSrcs = new Set(
        editor.dom.select('figure img').map(img => img.getAttribute('src')).filter(Boolean),
      );
      const images = editor.dom.select('img');
      images.forEach(img => {
        if (editor.dom.getParent(img, 'figure')) return;
        if (editor.dom.getParent(img, 'table')) return;
        const src = img.getAttribute('src');
        const parent = img.parentNode;

        let targetToReplace = img;
        if (parent && parent.nodeName === 'P') {
          if (parent.innerText.trim() === '') {
            targetToReplace = parent;
          } else {
            // img is mid-paragraph — split the p at the img position so the figure
            // doesn't end up as an invalid block child of the p.
            // Nodes after the img become a new trailing p, then img is moved out.
            const trailingNodes = [];
            let sibling = img.nextSibling;
            while (sibling) {
              trailingNodes.push(sibling);
              sibling = sibling.nextSibling;
            }
            if (trailingNodes.length > 0) {
              const trailingP = document.createElement('p');
              trailingNodes.forEach(n => trailingP.appendChild(n));
              parent.parentNode.insertBefore(trailingP, parent.nextSibling);
            }
            parent.parentNode.insertBefore(img, parent.nextSibling);
            if (parent.childNodes.length === 0) parent.remove();
          }
        }
        if (src && figuredSrcs.has(src)) {
          editor.dom.remove(targetToReplace);
          changed = true;
          return;
        }
        const figure = editor.dom.create('figure');
        const caption = editor.dom.create(
          'figcaption',
          { class: captionPlaceholderClass, contenteditable: 'true' },
          captionPlaceholderText,
        );
        const newImg = img.cloneNode(true);
        figure.appendChild(newImg);
        figure.appendChild(caption);
        editor.dom.replace(figure, targetToReplace);
        const emptyParagraph = editor.dom.create('p', null, '<br>');
        editor.dom.insertAfter(emptyParagraph, figure);
        changed = true;
      });
      return changed;
    };

    const extractAndSyncCaptions = () => {
      if (!isReady.value) return;
      const figures = editor.dom.select('figure');
      const myFigures = figures.map(figure => {
        const img = figure.querySelector('img');
        const figcaption = figure.querySelector('figcaption');
        if (img && figcaption) {
          const text = figcaption.innerText.trim();
          const isValidCaption = text !== '' && text !== captionPlaceholderText;
          return {
            editorId: props.id,
            src: img.getAttribute('src'),
            caption: isValidCaption ? text : '',
          };
        }
        return null;
      }).filter(item => item !== null && item.src);
      const otherFigures = (planStore.content.figures || []).filter(f => f.editorId !== props.id);
      planStore.content.figures = [...otherFigures, ...myFigures];
    };

    const initPlaceholderCaptions = () => {
      const captions = editor.dom.select('figcaption');
      captions.forEach(caption => {
        const text = caption.innerText.trim();
        if (text === '') {
          caption.innerText = captionPlaceholderText;
          editor.dom.addClass(caption, captionPlaceholderClass);
        }
      });
    };

    const ensureTrailingParagraphAfterTable = () => {
      const body = editor.getBody();
      const lastChild = body.lastElementChild;
      if (!lastChild || lastChild.nodeName !== 'TABLE') return;
      const emptyParagraph = editor.dom.create('p', null, '<br>');
      editor.dom.insertAfter(emptyParagraph, lastChild);
    };

    const updateWordCount = () => {
      if (!isReady.value) return;
      const count = editor.plugins.wordcount.body.getWordCount();
      currentWordCount.value = count;
      emit('update:wordCount', { id: props.id, count: count });
    };

    // TinyMCE 7 treats <figure class="image"> as its own caption widget (objectselected UI), causing a ghost on click.
    // We strip the class and contenteditable="false" so the figure is a plain editable block, then give figcaption
    // its own contenteditable="true" so focusout fires with e.target === figcaption.
    // Both attrs are excluded from extended_valid_elements so they never persist in saved content.
    editor.on('BeforeSetContent', (args) => {
      if (!props.allowImageUpload || !args.content) return;
      const contentContainer = document.createElement('div');
      contentContainer.innerHTML = args.content;
      contentContainer.querySelectorAll('figure').forEach(fig => {
        fig.classList.remove('image');
        fig.removeAttribute('contenteditable');
      });
      contentContainer.querySelectorAll('figcaption').forEach(cap => {
        cap.setAttribute('contenteditable', 'true');
      });
      args.content = contentContainer.innerHTML;
    });

    editor.on('init', () => {
      // TinyMCE converts upload errors into windowManager.alert() calls, stacking a second dialog
      // on top of the image dialog. Closing the alert pops both, dismissing the image dialog too.
      // Redirect to toasts instead so no dialog is stacked and the image dialog stays open.
      editor.windowManager.alert = (message, callback) => {
        if (message) toasts.error(message);
        if (callback) callback();
      };

      if (props.allowImageUpload) initPlaceholderCaptions();
      setTimeout(() => {
        isReady.value = true;
        updateWordCount();
      }, 100);
    });

    editor.on('keydown', (e) => {
      if (e.key === 'Enter') {
        const node = editor.selection.getNode();
        const figure = editor.dom.getParent(node, 'figure');
        if (figure) {
          e.preventDefault();
          const isInCaption = editor.dom.getParent(node, 'figcaption') || node.nodeName === 'FIGCAPTION';
          if (isInCaption) {
            let next = figure.nextSibling;
            if (!next || next.nodeName !== 'P') {
              next = editor.dom.create('p', null, '<br>');
              editor.dom.insertAfter(next, figure);
            }
            editor.selection.setCursorLocation(next, 0);
          } else {
            let prev = figure.previousSibling;
            if (!prev || prev.nodeName !== 'P') {
              prev = editor.dom.create('p', null, '<br>');
              figure.parentNode.insertBefore(prev, figure);
            }
            editor.selection.setCursorLocation(prev, 0);
          }
          return;
        }
      }
    });

    editor.on('keydown', (e) => {
      const allowedKeys = [8, 46, 37, 38, 39, 40];
      if (allowedKeys.includes(e.keyCode) || e.ctrlKey || e.metaKey) return;
      if (props.wordLimit && props.totalWordCount > props.wordLimit) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (props.wordLimit && props.totalWordCount === props.wordLimit) {
        if (e.key === ' ' || e.keyCode === 32) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    });

    editor.on('input change undo redo setcontent', () => {
      if (props.allowImageUpload) {
        cleanupEmptyFigures();
        cleanupDuplicateFigures();
        autoCaptionImages();
      }
      if (isDocumentContext && props.allowImageUpload) extractAndSyncCaptions();
      ensureTrailingParagraphAfterTable();
      updateWordCount();
    });

    editor.on('OpenWindow', () => {
      setTimeout(() => {
        const tabs = document.querySelectorAll('.tox-dialog__body-nav-item');
        const uploadTab = Array.from(tabs).find(t => t.innerText.trim() === 'Upload');
        uploadTab?.click();
      }, 50);
    });

    // On first click into the editor, NodeChange fires with selection at the body rather than the figcaption,
    // so the rAF-based placeholder clear below misses it. The click event fires reliably regardless of prior focus state.
    editor.on('click', (e) => {
      if (!props.allowImageUpload) return;
      const figcaption = editor.dom.getParent(e.target, 'figcaption');
      if (!figcaption) return;
      const text = figcaption.innerText.replace(/[​-‍﻿]/g, '').trim();
      if (text === captionPlaceholderText) {
        figcaption.innerHTML = '<br>';
        editor.dom.removeClass(figcaption, captionPlaceholderClass);
      }
    });

    // Accurate empty check based strictly on cursor movement
    editor.on('NodeChange', () => {
      requestAnimationFrame(() => {
        if (!editor || editor.removed || !editor.dom) return;
        editor.dom.select('figure').forEach(figure => {
          Array.from(figure.childNodes).forEach(child => {
            if (child.nodeName !== 'IMG' && child.nodeName !== 'FIGCAPTION') figure.removeChild(child);
          });
        });

        const currentNode = editor.selection.getNode();
        const allCaptions = editor.dom?.select('figcaption') || [];

        allCaptions.forEach(cap => {
          // editor.dom.isParent is not available in TinyMCE 7 — use native DOM contains() instead.
          const isUserInThisCaption = cap.contains(currentNode) || currentNode === cap;
          const text = cap.innerText.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();

          if (isUserInThisCaption) {
            if (text === captionPlaceholderText) {
              cap.innerHTML = '<br>';
              editor.dom.removeClass(cap, captionPlaceholderClass);

              if (editor.queryCommandState('Bold')) {
                editor.execCommand('Bold', false);
              }
              if (editor.queryCommandState('Italic')) {
                editor.execCommand('Italic', false);
              }
            }
          } else {
            // Check text length because `<br>` breaks .isEmpty()
            const isActuallyEmpty = text === '' || text === '\n';
            if (isActuallyEmpty && text !== captionPlaceholderText) {
              cap.innerHTML = captionPlaceholderText;
              editor.dom.addClass(cap, captionPlaceholderClass);
            }
          }
        });
      });
    });

    // NodeChange doesn't fire when focus leaves the editor entirely — focusout handles that case for caption placeholder restoration.
    editor.on('focusout', (e) => {
      const target = e.target;
      if (target.nodeName === 'FIGCAPTION') {
        const text = target.innerText.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
        if (text === '' || text === '\n') {
          editor.dom.addClass(target, captionPlaceholderClass);
          setTimeout(() => {
            target.innerText = captionPlaceholderText;
            extractAndSyncCaptions();
          }, 50);
        }
      }
    });

    // Restore placeholders correctly when clicking outside the editor entirely
    editor.on('blur', () => {
      if (!props.allowImageUpload) return;
      const allCaptions = editor.dom?.select('figcaption') || [];
      allCaptions.forEach(cap => {
        const text = cap.innerText.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
        if (text === '' || text === '\n') {
          cap.innerHTML = captionPlaceholderText;
          editor.dom.addClass(cap, captionPlaceholderClass);
        }
      });
      if (isDocumentContext) extractAndSyncCaptions();
    });
  },
}));
</script>
